import type { Action, Reducer } from "@reduxjs/toolkit";
import {
    StorylineState,
    StorylineActionTypes,
    DataSource,
    LOAD_STORYLINE,
    SHOW_STORYLINE,
    GO_TO_X_Y_Z,
    GO_TO_ID,
    PARAMETER_VALUE_UPDATED,
    DATASOURCE_UPDATED,
    DATASOURCE_ERROR_OCCURRED,
    UPDATE_CURRENT_SLIDE_TEMPLATE,
    UPDATE_SLIDE_TEMPLATES,
    UPDATE_CURRENT_FRAME_DATA,
    UPDATE_CANVAS_STATE,
    ADD_GLOBAL_DATASOURCE,
    UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE,
    Canvas
} from "./types";
import * as _ from "lodash";
import { MapWithPathSupport } from "../../shared/utilities";

const initialState: StorylineState = {
    id: "",
    name: "",
    storyline: { name: "", schemaVersion: "0.0.0", chapters: [], dataSources: {}, templates: {} },
    datasources: new Map<string, DataSource>(),
    datasourceValues: new Map<string, any>(),
    datasourceErrors: new Map<string, any>(),
    datasourcesInFlight: new Set<string>(),
    parameterValues: new MapWithPathSupport<any>(),
    canvases: [[[]]],
    chapterIndex: 0,
    pageIndex: 0,
    paragraphIndex: 0,
    currentParagraph: undefined,
    loading: true,
    canNavigateBackwards: false,
    canNavigateForward: false,
    canvasState: {}
}

const hasMissingDatasourceValues = (dataSources: Map<string, DataSource>, dataSourceValues: Map<string, any>, parameterValues: MapWithPathSupport<any>): boolean => {
    // Get all data sources which need to be populated before we can perform the initial render...
    const requiredDataSources = Array.from(dataSources.values())
        .filter(ds => !ds.allowLazyLoading)
        .filter(ds => ds.autoRefresh)
        .filter(ds => ds.parameters.filter(p => p.isRequired).every(p => parameterValues.get(p.name) !== undefined));

    return requiredDataSources.some(ds => !dataSourceValues.has(ds.name));
};

export const storylineReducer: Reducer<StorylineState, Action, StorylineState> = (
    state = initialState,
    action: StorylineActionTypes
): StorylineState => {
    switch (action.type) {
        case LOAD_STORYLINE:
            return {
                ...initialState,
                id: action.id
            };

        case SHOW_STORYLINE: {
            const loading = hasMissingDatasourceValues(action.dataSources, action.dataSourceValues, action.parameterValues);
            const storylineCanvases = action.storyline.chapters.map(chapter =>
                chapter.pages.map(page =>
                    page.paragraphs.map(paragraph => {
                        const paragraphData = Object.fromEntries(paragraph.slots.map(slot => {
                            return [slot.name, action.dataSourceValues.get(slot.dataSource)];
                        }));

                        return ({
                            name: paragraph.name,
                            chapterName: chapter.name,
                            pageName: page.name,
                            title: paragraphData?.["title"],
                            data: paragraphData,
                            template: action.storyline.templates[page.template],
                            templateName: page.template
                        } as Canvas);
                    })
                )
            );

            return {
                id: action.storyline.name,
                name: action.storyline.name,
                storyline: action.storyline,
                canvases: storylineCanvases,
                datasources: action.dataSources,
                datasourceValues: action.dataSourceValues,
                datasourceErrors: state.datasourceErrors,
                datasourcesInFlight: state.datasourcesInFlight,
                parameterValues: action.parameterValues,
                chapterIndex: 0,
                pageIndex: 0,
                paragraphIndex: 0,
                currentParagraph: getFrame(storylineCanvases, 0, 0, 0),
                loading: loading,
                ...calculateNavigationProperties(storylineCanvases, 0, 0, 0),
                canvasState: {}
            };
        }

        case PARAMETER_VALUE_UPDATED:
            state.parameterValues.set(action.parameterName, action.newValue);
            return {
                ...state,
                parameterValues: new MapWithPathSupport(state.parameterValues)
            };

        case DATASOURCE_UPDATED: {
            state.datasourceValues.set(action.datasourceName, action.data);
            state.datasourceErrors.delete(action.datasourceName);

            // Short-circuit heavy canvas data calculations if we're not ready to render the page yet...
            const loading = state.loading && hasMissingDatasourceValues(state.datasources, state.datasourceValues, state.parameterValues);
            if (loading) return state;

            const postUpdateCanvases = state.storyline.chapters.map((chapter, chapterIndex) =>
                chapter.pages.map((page, pageIndex) =>
                    page.paragraphs.map((paragraph, paragraphIndex) => {
                        // Reuse the existing paragraph object when a rebuild would produce identical content -
                        // reference stability lets downstream memoization (Canvas, minimap previews) skip re-renders...
                        const previous = state.canvases?.[chapterIndex]?.[pageIndex]?.[paragraphIndex];
                        if (previous &&
                            previous.template === state.storyline.templates[page.template] &&
                            paragraph.slots.every(slot => previous.data?.[slot.name] === state.datasourceValues.get(slot.dataSource))) {
                            return previous;
                        }

                        const paragraphData = Object.fromEntries(paragraph.slots.map(slot => {
                            return [slot.name, state.datasourceValues.get(slot.dataSource)];
                        }));

                        return ({
                            name: paragraph.name,
                            chapterName: chapter.name,
                            pageName: page.name,
                            title: paragraphData?.["title"],
                            data: paragraphData,
                            template: state.storyline.templates[page.template],
                            templateName: page.template
                        } as Canvas);
                    })
                )
            );

            return {
                ...state,
                loading: false,
                canvases: postUpdateCanvases,
                currentParagraph: getFrame(postUpdateCanvases, state.chapterIndex, state.pageIndex, state.paragraphIndex)
            };
        }

        case DATASOURCE_ERROR_OCCURRED:
            state.datasourceErrors.set(action.datasourceName, action.error);

            return {
                ...state,
                datasourceErrors: new Map(state.datasourceErrors),
            };


        case GO_TO_X_Y_Z:
            return {
                ...state,
                chapterIndex: action.chapterIndex,
                pageIndex: action.pageIndex,
                paragraphIndex: action.paragraphIndex,
                currentParagraph: getFrame(state.canvases, action.chapterIndex, action.pageIndex, action.paragraphIndex),
                ...calculateNavigationProperties(state.canvases, action.chapterIndex, action.pageIndex, action.paragraphIndex)
            };

        case GO_TO_ID:

            const getCoordinatesForName = (name: string) => {
                for (const [chapterIndex, chapter] of state.canvases.entries()) {
                    for (const [pageIndex, page] of chapter.entries()) {
                        for (const [paragraphIndex, paragraph] of page.entries()) {
                            if (paragraph.name === name) return [chapterIndex, pageIndex, paragraphIndex];
                        }
                    }
                }
            }

            const matchingCoordinates = getCoordinatesForName(action.id);

            if (matchingCoordinates) {
                const [chapterIndex, pageIndex, paragraphIndex] = matchingCoordinates;
                return {
                    ...state,
                    chapterIndex,
                    pageIndex,
                    paragraphIndex,
                    currentParagraph: getFrame(state.canvases, chapterIndex, pageIndex, paragraphIndex),
                    ...calculateNavigationProperties(state.canvases, chapterIndex, pageIndex, paragraphIndex)
                };
            }
            else {
                console.log(`No slide with Name, ID or Canvas ID = "${action.id}" found.`);
                return state;
            }

        case UPDATE_CURRENT_SLIDE_TEMPLATE:
            // Determine the new version before mutating below - the map entry may alias the same object as currentParagraph.template...
            const previousTemplate = state.storyline.templates[state.currentParagraph.templateName];
            const templateChanged = previousTemplate?.jsx !== action.newTemplate || previousTemplate?.css !== action.newCustomCss;
            const newVersion = (previousTemplate?.version ?? 0) + (templateChanged ? 1 : 0);

            state.currentParagraph.template.jsx = action.newTemplate;
            state.currentParagraph.template.css = action.newCustomCss;
            state.currentParagraph.template.version = newVersion;

            return {
                ...state,
                storyline: {
                    ...state.storyline,
                    templates: {
                        ...state.storyline.templates,
                        [state.currentParagraph.templateName]: {
                            jsx: action.newTemplate,
                            css: action.newCustomCss,
                            version: newVersion
                        }
                    }
                },
                currentParagraph: {
                    ...state.currentParagraph,
                    template: {
                        css: action.newCustomCss,
                        jsx: action.newTemplate,
                        version: newVersion
                    }
                }
            };

        case UPDATE_SLIDE_TEMPLATES:
            const versionedTemplates = _.mapValues(action.newTemplates, (template, name) => {
                const previous = state.storyline.templates[name];
                const changed = previous?.jsx !== template.jsx || previous?.css !== template.css;

                return { ...template, version: (previous?.version ?? 0) + (changed ? 1 : 0) };
            });
            const allTemplates = { ...state.storyline.templates, ...versionedTemplates };

            return {
                ...state,
                storyline: {
                    ...state.storyline,
                    templates: allTemplates
                },
                currentParagraph: {
                    ...state.currentParagraph,
                    template: allTemplates[state.currentParagraph.templateName]
                }
            };

        case UPDATE_CURRENT_FRAME_DATA:
            return {
                ...state,
                currentParagraph: {
                    ...state.currentParagraph,
                    data: action.newData
                }
            }

        case UPDATE_CANVAS_STATE:
            return {
                ...state,
                canvasState: action.newState
            }

        case ADD_GLOBAL_DATASOURCE:
            state.datasourceValues.set(action.datasource.name, action.datasource.defaultData);

            return {
                ...state,
                storyline: {
                    ...state.storyline,
                    dataSources: {
                        ...state.storyline.dataSources,
                        [action.datasource.name]: action.datasource
                    }
                }
            };

        case UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE:
            const datasourceName = action.datasource.name;

            // New in-flight request...
            if (action.inFlightRequestCount > 0 && !state.datasourcesInFlight.has(datasourceName)) {
                return {
                    ...state,
                    datasourcesInFlight: new Set<string>([...state.datasourcesInFlight.values(), datasourceName])
                };
            }

            // In-flight request completed...
            if (action.inFlightRequestCount === 0 && state.datasourcesInFlight.has(datasourceName)) {
                return {
                    ...state,
                    datasourcesInFlight: new Set<string>([...state.datasourcesInFlight.values()].filter(ds => ds !== datasourceName))
                }
            }

            // No material change...
            return state;

        default:
            return state;
    }
}

function getFrame(canvases: Canvas[][][], chapterIndex: number, pageIndex: number, paragraphIndex: number): Canvas {
    return canvases?.[chapterIndex]?.[pageIndex]?.[paragraphIndex] ?? {
        name: "",
        chapterName: "",
        pageName: "",
        title: "",
        data: {},
        template: {
            css: "",
            jsx: "",
            version: 0
        },
        templateName: ""
    };
}

function calculateNavigationProperties(canvases: Canvas[][][], chapterIndex: number, pageIndex: number, paragraphIndex: number) {
    return {
        canNavigateBackwards: chapterIndex > 0 || pageIndex > 0 || paragraphIndex > 0,
        canNavigateForward: canvases?.length > (chapterIndex + 1) || canvases?.[chapterIndex]?.length > (pageIndex + 1) || canvases?.[chapterIndex]?.[pageIndex]?.length > (paragraphIndex + 1)
    };
}

